/**
 * Client du proxy-server (Express sur Infomaniak) — même rôle que GameTime/proxyApi.js.
 * Les routes /ai/* et /account/* exigent un Firebase ID token (Authorization: Bearer).
 * Le serveur fait autorité sur les quotas (gratuit / Premium) : le client ne fait
 * qu'afficher ce que le serveur renvoie. Un jeton App Check est joint quand disponible.
 */
import { auth, getAppCheckToken } from "../firebase";
import { track } from "./analytics";
import type { LearningProfile } from "./progressService";

export const PROXY_BASE_URL = (process.env.REACT_APP_PROXY_BASE_URL || "http://localhost:3001").replace(/\/$/, "");

export type ChatRole = "user" | "assistant";
export interface ChatMessage {
  role: ChatRole;
  content: string;
  at?: number;
}
export type ChatAction = "simplify" | "example" | "daily-life" | "similar" | "younger";

export interface AiUsage {
  used: number;
  limit: number | null; // null = illimité (Premium)
  premium: boolean;
}

export interface AiChatResponse {
  reply: string;
  usage: AiUsage;
}

// Cours du prof IA (docs/PROF-IA-VIDEO.md §2) — script généré côté serveur, mis en cache.
export type LessonLevel = "quick" | "standard" | "detailed";
export type LessonMood = "explain" | "point" | "think" | "celebrate" | "warn";

export interface LessonSegment {
  id: string;
  /** Ce que le prof DIT (voix + sous-titres) : 1 à 3 phrases, oral, tutoiement, sans markdown */
  say: string;
  /** Ce qui s'écrit au TABLEAU pendant qu'il parle : markdown court, peut être vide */
  board?: string;
  /** Élément à mettre en évidence au tableau (mot ou expression présent dans `board`) */
  emphasis?: string;
  /** Geste / attitude du personnage */
  mood?: LessonMood;
}

export interface Lesson {
  chapterId: string;
  level: LessonLevel;
  title: string;
  /** Accroche orale d'ouverture (1-2 phrases) */
  intro: string;
  segments: LessonSegment[];
  /** Résumé oral de fin (2-3 phrases) */
  recap: string;
  /** Question de contrôle finale */
  check: { question: string; choices: string[]; answer: number; feedback: string };
  /** Signature du profil utilisée pour le cache */
  profileKey: string;
  generatedAt: number;
}

export interface LessonResponse {
  lesson: Lesson;
  /** true = servi depuis le cache serveur : ne consomme pas de quota */
  cached: boolean;
  usage: AiUsage;
}

export class ProxyError extends Error {
  status: number;
  code: string | null;
  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function baseHeaders(withAuth: boolean): Promise<Record<string, string>> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  const ac = await getAppCheckToken();
  if (ac) h["X-Firebase-AppCheck"] = ac;
  if (withAuth) {
    const user = auth.currentUser;
    if (!user) throw new ProxyError("Non connecté", 401, "unauthenticated");
    h.Authorization = `Bearer ${await user.getIdToken()}`;
  }
  return h;
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let data: { error?: string; code?: string } = {};
    try {
      data = await res.json();
    } catch (e) {
      /* corps non JSON */
    }
    throw new ProxyError(data.error || `Erreur serveur (${res.status})`, res.status, data.code || null);
  }
  return (await res.json()) as T;
}

async function post<T>(path: string, body: unknown, withAuth = true): Promise<T> {
  const res = await fetch(`${PROXY_BASE_URL}${path}`, { method: "POST", headers: await baseHeaders(withAuth), body: JSON.stringify(body) });
  return handle<T>(res);
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${PROXY_BASE_URL}${path}`, { headers: await baseHeaders(true) });
  return handle<T>(res);
}

// ---------------------------------------------------------------------------
// Professeur IA
// ---------------------------------------------------------------------------
export async function aiChat(params: {
  chapterId: string;
  messages: ChatMessage[];
  action?: ChatAction;
  year?: number;
  learningProfile?: LearningProfile;
}): Promise<AiChatResponse> {
  track("chat_message_sent", { action: params.action || "free" });
  return post<AiChatResponse>("/ai/chat", {
    chapterId: params.chapterId,
    year: params.year,
    action: params.action,
    learningProfile: params.learningProfile,
    messages: params.messages.slice(-12).map((m) => ({ role: m.role, content: m.content })),
  });
}

export async function aiExplainError(params: {
  chapterId: string;
  exerciseId: string;
  studentAnswer: string;
  year?: number;
  learningProfile?: LearningProfile;
}): Promise<AiChatResponse> {
  return post<AiChatResponse>("/ai/explain-error", params);
}

/** Script du cours animé. `cached: true` (cache serveur) ne consomme pas de quota. */
export async function getLesson(params: { chapterId: string; level?: LessonLevel; year?: number; learningProfile?: LearningProfile }): Promise<LessonResponse> {
  return post<LessonResponse>("/ai/lesson", params);
}

export async function getAiUsage(): Promise<AiUsage> {
  return get<AiUsage>("/ai/usage");
}

// ---------------------------------------------------------------------------
// Auth Discord (OAuth côté serveur) et compte
// ---------------------------------------------------------------------------
export async function discordStart(params: { platform: "web" | "native"; link: boolean }): Promise<{ url: string }> {
  return post<{ url: string }>("/auth/discord/start", params, params.link);
}

export async function discordExchange(code: string): Promise<{ token: string; linked: boolean }> {
  return post<{ token: string; linked: boolean }>("/auth/discord/exchange", { code }, false);
}

export async function deleteAccountOnServer(): Promise<void> {
  await post<{ ok: boolean }>("/account/delete", {});
}

export async function checkProxyHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${PROXY_BASE_URL}/health`);
    const data = await res.json();
    return data.status === "ok";
  } catch (e) {
    return false;
  }
}
